import {classNames} from "@progress/kendo-react-common"
import {
    CalendarCellProps,
    CalendarProps,
    DatePicker,
    DatePickerChangeEvent,
    MultiViewCalendar,
} from "@progress/kendo-react-dateinputs"
import {FieldRenderProps} from "@progress/kendo-react-form"
import {Tooltip} from "@progress/kendo-react-tooltip"
import dayjs from "dayjs"
import React, {createContext, useEffect} from "react"
import {UpdateScheduleModel} from "../../../containers/DVSASchedules/schedulesSlice"
import {setNewBKStartDate, setScheduleForDay} from "../../../containers/MotBooking/bookingSlice"
import {RootState} from "../../../rootReducer"
import {useAppDispatch, useAppSelector} from "../../../store"

const DateContext = createContext({
    testDate: new Date(),
    updateDatePickerValue: (date: Date) => {},
})

const CustomCell = (props: CalendarCellProps) => {
    const dateView = props.view === 0
    const schedulesForDealer = useAppSelector((state: RootState) => state.application.schedulesForDealer)
    const {updateDatePickerValue} = React.useContext(DateContext)
    const noScheduleTitle = "No Schedule for the day"
    const scheduleIndex = schedulesForDealer
        .map(x => dayjs(x.startDateTime).format("DD/MM/YYYY"))
        .indexOf(dayjs(props.value).format("DD/MM/YYYY"))

    const hasScheduleForDay = scheduleIndex >= 0

    const handleClick = (ev: any) => {
        if (hasScheduleForDay && dateView) {
            if (props.onClick) {
                props.onClick(props.value)
                updateDatePickerValue(props.value)
            }
        }
    }

    const style = {
        cursor: !hasScheduleForDay && dateView ? "not-allowed" : "pointer",
        opacity: !hasScheduleForDay && dateView ? 0.6 : 1,
    }

    const className = classNames({
        "k-calendar-td": true,
        "k-selected": props.isSelected,
        "k-today": props.isToday,
        "k-focus": props.isFocused,
    })

    const titleValue: string = !hasScheduleForDay && dateView ? noScheduleTitle : ""
    return (
        <td onClick={handleClick} className={className}>
            <span className="k-link" style={style} title={titleValue}>
                {props.children}
            </span>
        </td>
    )
}

const CustomCalendar = (props: CalendarProps) => {
    const {min, value, focusedDate} = props
    const {updateDatePickerValue} = React.useContext(DateContext)
    const onchangeCalendar = (ev: any) => {
        updateDatePickerValue(ev.value)
    }

    return (
        <Tooltip anchorElement="target" position="top">
            <MultiViewCalendar
                onChange={onchangeCalendar}
                views={1}
                bottomView="month"
                topView="month"
                cell={CustomCell}
                value={value}
                min={min}
                focusedDate={focusedDate}
            />
        </Tooltip>
    )
}

const BKGridFilterDatePicker = (fieldRenderProps: FieldRenderProps) => {
    const dispatch = useAppDispatch()
    const {name, label, val, min, disabled, updateStateValue, schedulesData} = fieldRenderProps

    const [showDatePicker, setShowDatePicker] = React.useState<boolean>(false)
    const [testDate] = React.useState<Date>(dayjs(val).toDate())

    const hasScheduleForDay =
        schedulesData
            .map((x: UpdateScheduleModel) => dayjs(x.startDateTime).format("DD/MM/YYYY"))
            .indexOf(dayjs(val).format("DD/MM/YYYY")) >= 0

    const updateDatePickerValue = (testDate: Date) => {
        updateStateValue(testDate)
        dispatch(setNewBKStartDate(dayjs(testDate).toString()))

        setShowDatePicker(false)
    }

    useEffect(() => {
        dispatch(setScheduleForDay(hasScheduleForDay))
    }, [hasScheduleForDay])

    const onDateChange = (event: DatePickerChangeEvent) => {
        if (dayjs(event.target.value).startOf("day") >= dayjs().startOf("day")) {
            if (event.nativeEvent.target.tagName === "INPUT") {
                return false
            }
        }
    }

    const onDateBlur = (event: any) => {
        setShowDatePicker(false)
    }

    const dateProviderProps = {testDate, updateDatePickerValue}
    return (
        <DateContext.Provider value={dateProviderProps}>
            <DatePicker
                name={name}
                label={label}
                min={min}
                disabled={disabled}
                show={showDatePicker}
                value={testDate}
                onFocus={() => {
                    setShowDatePicker(true)
                }}
                onBlur={onDateBlur}
                onChange={onDateChange}
                calendar={CustomCalendar}
                focusedDate={testDate}
            />
        </DateContext.Provider>
    )
}

export default BKGridFilterDatePicker
